import {
  CreateTransactionInput,
  CreateTransactionMutationVariables,
  CustomSubscription,
  CustomSubscriptionType,
  DeepDeletableEntityType,
  DeleteEntityByIdMutationVariables,
  MutationStatus,
  OnCreateCustomSubscriptionSubscription,
  OnCreateTransactionSubscription,
  OnDeleteCustomSubscriptionSubscription,
  OnDeleteTransactionSubscription,
  OnUpdateCustomSubscriptionSubscription,
  OnUpdateTransactionSubscription,
  TRANSACTION_TABLE_NAME,
  Transaction,
  TransactionLinkType,
  TransactionStatus,
  UpdateTransactionInput,
  cleanInputCreate,
  cleanInputUpdate,
  createTransaction as createTransactionMutation,
  deleteEntityById as deleteEntityByIdMutation,
  getReadId,
  getTableClientId,
  getTransaction as getTransactionQuery,
  getTypedCustomSubscriptionValueForRefetch,
  onCreateCustomSubscription,
  onCreateTransaction,
  onDeleteCustomSubscription,
  onDeleteTransaction,
  onUpdateCustomSubscription,
  onUpdateTransaction,
  type Posting,
  updateTransaction as updateTransactionMutation,
  BankAccount,
  removeNestedFieldsExcept,
  isPostingReversalOrCancelledByReversal,
  TransactionWithoutPostings,
  isTransactionSnoozed,
  isTransactionToReconcile,
  mergeFullyFetchedRecords,
} from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import { get, isConditionalCheckFailedGraphQLError, list, mutation, useSubscriptions } from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import { Reducer, createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { syncTransactions } from 'src/tenantHooks/graphqlHelper';
import {
  PendingFastValidation,
  forgetPendingFastValidation,
  rememberPendingFastValidation,
} from 'src/utils/pendingFastValidationCache';

import { useBankAccounts } from './BankAccountsContext';
import { useLeases } from './LeasesContext';
import { invalidatePayments, isIncomingTransactionNewlyReconciled } from './paymentsInvalidation';
import { fetchPostings } from './PostingsContext';
import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';
import {
  TransactionsFetchRequest,
  addTransactionsFetchRequest,
  filterTransactionsByRightsAndLeases,
  mergeTransactionsFetchedById,
} from './utils/transactionsStateUtils';

export interface ExtendedTransaction extends Transaction {
  postings: Posting[];
}

interface TransactionWithoutPostingsWithItemsPostings extends TransactionWithoutPostings {
  postings: { items: Posting[] };
}
export type TransactionWithoutPostingsAndBankAccount = Omit<TransactionWithoutPostings, 'bankAccount'>;
type TransactionWithoutBankAccount = Omit<Transaction, 'bankAccount'>;

interface TransactionPostingsOptions {
  includesDraftPostings?: boolean;
}

export const addPostingsToTransaction = async (
  transaction: TransactionWithoutPostings,
  options?: TransactionPostingsOptions
) => ({
  ...transaction,
  postings: await fetchPostings('byTransaction', transaction.id, undefined, undefined, options),
});

export const addPostingsToTransactions = async (
  transactions: TransactionWithoutPostings[],
  options?: TransactionPostingsOptions
) => Promise.all(transactions.map((transaction) => addPostingsToTransaction(transaction, options)));

const withActivePostings = (result: TransactionWithoutPostingsWithItemsPostings): TransactionWithoutBankAccount => ({
  ...result,
  postings: (result.postings?.items ?? []).filter(
    (posting) => posting._deleted !== true && !isPostingReversalOrCancelledByReversal(posting)
  ),
});

export const fetchTransaction = async (id: string): Promise<TransactionWithoutBankAccount> =>
  withActivePostings(await get<TransactionWithoutPostingsWithItemsPostings>(getTransactionQuery, id));

export const fetchTransactionIfExists = async (id: string): Promise<TransactionWithoutBankAccount | null> => {
  const result: TransactionWithoutPostingsWithItemsPostings | null =
    await get<TransactionWithoutPostingsWithItemsPostings>(getTransactionQuery, id);
  return isNil(result) ? null : withActivePostings(result);
};

export const OPTIMISTIC_RECONCILIATION_UNDO_DELAY_MS = 5000;

export interface OptimisticReconciliationInput {
  transactionId: string;
  pendingFastValidation: PendingFastValidation;
  execute: () => Promise<boolean>;
  onSettled?: (reconciliationSucceeded: boolean) => void;
}

export interface TransactionContext {
  transactions: TransactionWithoutPostings[] | null;
  transactionsHistory: TransactionWithoutPostings[];
  transactionsToCheck: TransactionWithoutPostings[];
  transactionsSnoozed: TransactionWithoutPostings[];
  transactionsExcluded: TransactionWithoutPostings[];
  transactionRefetch: (transactionIds?: string[]) => void;
  refreshTransaction: (transactionId: string) => Promise<void>;
  createTransaction: (
    input: CreateTransactionInput | Omit<CreateTransactionInput, 'clientId' | 'readId'>
  ) => Promise<TransactionWithoutPostings>;
  updateTransaction: (updates: UpdateTransactionInput) => Promise<TransactionWithoutPostings>;
  deepDeleteTransaction: (id: string) => Promise<MutationStatus | undefined>;
  getTransaction: (id: string) => TransactionWithoutPostings | undefined;
  getTransactionsForLease: (leaseId: string, filterIgnoredTransaction?: boolean) => TransactionWithoutPostings[];
  getSplittedTransactionsForLease: (leaseId: string) => Promise<ExtendedTransaction[]>;
  scheduleOptimisticReconciliation: (input: OptimisticReconciliationInput) => void;
  cancelOptimisticReconciliation: (transactionId: string) => boolean;
  markFastValidationRejected: (transactionId: string) => void;
  isFastValidationRejected: (transactionId: string) => boolean;
  transactionsLoading: boolean | undefined;
  transactionsError: string | undefined;
}

type Action =
  | {
      type: 'SHOULD_FETCH' | 'IS_FETCHING';
      payload: { transactionIds?: string[] };
    }
  | {
      type: 'FETCHED' | 'FETCHED_BY_ID';
      payload: { transactions: TransactionWithoutPostings[] };
    }
  | {
      type: 'ADD_TRANSACTION' | 'UPDATE_TRANSACTION';
      payload: { transaction: TransactionWithoutPostings };
    }
  | {
      type: 'ERROR';
      payload: { error: string };
    }
  | {
      type: 'DELETE_TRANSACTION';
      payload: { id: string };
    };

interface PendingOptimisticReconciliation {
  timer: ReturnType<typeof setTimeout>;
  running: boolean;
  pendingFastValidation: PendingFastValidation;
  execute: () => Promise<boolean>;
  onSettled?: (reconciliationSucceeded: boolean) => void;
}

interface TransactionState {
  transactions: TransactionWithoutPostings[] | null;
  loading: boolean;
  fetching: boolean;
  shouldFetchState: TransactionsFetchRequest;
  error: string | undefined;
}

const initialState: TransactionState = {
  transactions: null,
  loading: false,
  fetching: false,
  shouldFetchState: { shouldFetch: false, transactionIdsToFetched: undefined },
  error: undefined,
};

const transactionActionReducer = (state: TransactionState, action: Action): TransactionState => {
  switch (action.type) {
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        fetching: false,
        error: undefined,
        transactions: mergeFullyFetchedRecords(state.transactions, action.payload.transactions),
      };
    case 'FETCHED_BY_ID':
      return {
        ...state,
        loading: false,
        fetching: false,
        error: undefined,
        transactions: mergeTransactionsFetchedById(state.transactions, action.payload.transactions),
      };
    case 'SHOULD_FETCH': {
      const isInitialLoadRunning = isNil(state.transactions) && state.fetching;
      if (isInitialLoadRunning) return state;
      return {
        ...state,
        shouldFetchState: addTransactionsFetchRequest(state.shouldFetchState, action.payload.transactionIds),
      };
    }
    case 'IS_FETCHING':
      return {
        ...state,
        loading: isNil(state.transactions),
        fetching: true,
        shouldFetchState: { shouldFetch: false },
      };
    case 'ERROR':
      return { ...state, loading: false, fetching: false, error: action.payload.error };
    case 'ADD_TRANSACTION':
      if (!state.transactions) return state;
      return {
        ...state,
        transactions: mergeTransactionsFetchedById(state.transactions, [action.payload.transaction]),
      };
    case 'DELETE_TRANSACTION':
      if (!state.transactions) return state;
      return {
        ...state,
        transactions: state.transactions.filter((t) => t.id !== action.payload.id),
      };
    case 'UPDATE_TRANSACTION': {
      if (!state.transactions) return state;
      const updatedTransaction = action.payload.transaction;
      return {
        ...state,
        transactions: state.transactions.map((transaction) => {
          const isOlderThanState = (updatedTransaction._version ?? 0) < (transaction._version ?? 0);
          if (transaction.id === updatedTransaction.id && !isOlderThanState) {
            return { ...transaction, ...updatedTransaction } as TransactionWithoutPostings;
          }
          return transaction;
        }),
      };
    }
    default:
      return state;
  }
};

type TransactionBuckets = [
  TransactionWithoutPostings[],
  TransactionWithoutPostings[],
  TransactionWithoutPostings[],
  TransactionWithoutPostings[],
];

const classifyTransactions = (
  transactions: TransactionWithoutPostings[]
): Pick<
  TransactionContext,
  'transactionsHistory' | 'transactionsExcluded' | 'transactionsToCheck' | 'transactionsSnoozed'
> => {
  const [transactionsToCheck, transactionsHistory, transactionsExcluded, transactionsSnoozed] =
    transactions.reduce<TransactionBuckets>(
      (acc, transaction) => {
        if (isTransactionSnoozed(transaction)) {
          acc[3].push(transaction);
        } else if (isTransactionToReconcile(transaction)) {
          acc[0].push(transaction);
        } else if (
          transaction.status === TransactionStatus.RECONCILED ||
          transaction.status === TransactionStatus.PARTIALLY_RECONCILED
        ) {
          acc[1].push(transaction);
          if (transaction.status === TransactionStatus.PARTIALLY_RECONCILED && (transaction.links?.length ?? 0) > 1) {
            acc[0].push(transaction);
          }
        } else if (transaction.status === TransactionStatus.EXCLUDED) {
          acc[2].push(transaction);
        }
        return acc;
      },
      [[], [], [], []]
    );
  return { transactionsHistory, transactionsToCheck, transactionsExcluded, transactionsSnoozed };
};

const fetchTransactions = async (clientId: string): Promise<TransactionWithoutPostingsAndBankAccount[]> =>
  await list<TransactionWithoutPostingsAndBankAccount>(
    syncTransactions,
    'clientId',
    getTableClientId(clientId, TRANSACTION_TABLE_NAME)
  );

function addBankAccountToTransaction(
  transaction: TransactionWithoutPostingsAndBankAccount,
  bankAccounts: BankAccount[]
): TransactionWithoutPostings;
function addBankAccountToTransaction(
  transaction: TransactionWithoutBankAccount,
  bankAccounts: BankAccount[]
): Transaction;
function addBankAccountToTransaction(
  transaction: TransactionWithoutPostingsAndBankAccount | TransactionWithoutBankAccount,
  bankAccounts: BankAccount[]
): TransactionWithoutPostings | Transaction {
  return {
    ...transaction,
    bankAccount: bankAccounts.find((currentBankAccount) => currentBankAccount.id === transaction.bankAccountId),
  };
}

export const TransactionContext = createContext<TransactionContext | null>(null);

export const TransactionContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<TransactionState, Action>>(transactionActionReducer, initialState);
  const { clientId, isOwner, userId, rootUser, isFetchingUser } = useUser();
  const { leases, leasesLoading } = useLeases();
  const { loading: bankAccountLoading, bankAccounts } = useBankAccounts();
  const { financialTransactionsRead, financialTransactionsDelete, loading: permissionsLoading } = usePermissions();
  const [optimisticallyReconciledIds, setOptimisticallyReconciledIds] = useState<string[]>([]);
  const [settledReconciliationsCount, setSettledReconciliationsCount] = useState<number>(0);
  const pendingReconciliationsRef = useRef<Map<string, PendingOptimisticReconciliation>>(new Map());
  const rejectedFastValidationIdsRef = useRef<Set<string>>(new Set());
  const storedTransactionsRef = useRef<TransactionWithoutPostings[] | null>(state.transactions);
  const queryClient = useQueryClient();

  useEffect(() => {
    storedTransactionsRef.current = state.transactions;
  }, [state.transactions]);
  const transactions = useMemo<TransactionWithoutPostings[] | null>(() => {
    if (isNil(state.transactions)) {
      return state.transactions;
    }
    const bankAccountsById = new Map(bankAccounts.map((bankAccount) => [bankAccount.id, bankAccount]));
    const withBankAccount = state.transactions.map(
      (transaction): TransactionWithoutPostings => ({
        ...transaction,
        bankAccount: bankAccountsById.get(transaction.bankAccountId),
      })
    );
    const visibleLeaseIds = new Set(leases.map((lease) => lease.id));
    return filterTransactionsByRightsAndLeases(withBankAccount, rootUser, financialTransactionsRead, visibleLeaseIds);
  }, [state.transactions, bankAccounts, rootUser, financialTransactionsRead, leases]);

  const visibleTransactions = useMemo<TransactionWithoutPostings[]>(() => {
    const allTransactions = transactions ?? [];
    if (isEmpty(optimisticallyReconciledIds)) {
      return allTransactions;
    }
    return allTransactions.filter((transaction) => !optimisticallyReconciledIds.includes(transaction.id));
  }, [transactions, optimisticallyReconciledIds]);

  const { transactionsHistory, transactionsToCheck, transactionsExcluded, transactionsSnoozed } = useMemo(
    () => classifyTransactions(visibleTransactions),
    [visibleTransactions]
  );
  const loading = bankAccountLoading || isFetchingUser || permissionsLoading || leasesLoading;

  useEffect(() => {
    setOptimisticallyReconciledIds((previousIds) => {
      if (isEmpty(previousIds)) {
        return previousIds;
      }
      const keptIds = previousIds.filter((transactionId) => {
        if (pendingReconciliationsRef.current.has(transactionId)) {
          return true;
        }
        const transaction = (state.transactions ?? []).find(
          (currentTransaction) => currentTransaction.id === transactionId
        );
        return !isNil(transaction) && (isTransactionToReconcile(transaction) || isTransactionSnoozed(transaction));
      });
      return keptIds.length === previousIds.length ? previousIds : keptIds;
    });
  }, [state.transactions, settledReconciliationsCount]);

  const refreshTransaction = async (transactionId: string): Promise<void> => {
    try {
      const refreshedTransaction = await fetchTransaction(transactionId);
      dispatch({
        type: 'UPDATE_TRANSACTION',
        payload: { transaction: addBankAccountToTransaction(refreshedTransaction, bankAccounts) },
      });
    } catch (error) {
      console.error('Could not read the transaction back', error);
    }
  };

  const runOptimisticReconciliation = async (transactionId: string) => {
    const pendingReconciliation = pendingReconciliationsRef.current.get(transactionId);
    if (isNil(pendingReconciliation) || pendingReconciliation.running) {
      return;
    }
    clearTimeout(pendingReconciliation.timer);
    pendingReconciliation.running = true;
    let reconciliationSucceeded = false;
    try {
      reconciliationSucceeded = await pendingReconciliation.execute();
    } catch (error) {
      console.error('Error running optimistic reconciliation', error);
    }
    pendingReconciliationsRef.current.delete(transactionId);
    if (reconciliationSucceeded) {
      forgetPendingFastValidation(transactionId);
      await refreshTransaction(transactionId);
    } else {
      setOptimisticallyReconciledIds((previousIds) => previousIds.filter((id) => id !== transactionId));
    }
    setSettledReconciliationsCount((previousCount) => previousCount + 1);
    pendingReconciliation.onSettled?.(reconciliationSucceeded);
  };

  useEffect(() => {
    const warnAboutPendingReconciliations = (event: BeforeUnloadEvent) => {
      if (isEmpty(pendingReconciliationsRef.current)) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };
    const flushPendingReconciliations = () => {
      [...pendingReconciliationsRef.current.keys()].forEach((transactionId) => {
        runOptimisticReconciliation(transactionId);
      });
    };
    window.addEventListener('beforeunload', warnAboutPendingReconciliations);
    window.addEventListener('pagehide', flushPendingReconciliations);
    return () => {
      window.removeEventListener('beforeunload', warnAboutPendingReconciliations);
      window.removeEventListener('pagehide', flushPendingReconciliations);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleOptimisticReconciliation = ({
    transactionId,
    pendingFastValidation,
    execute,
    onSettled,
  }: OptimisticReconciliationInput) => {
    const timer = setTimeout(() => {
      runOptimisticReconciliation(transactionId);
    }, OPTIMISTIC_RECONCILIATION_UNDO_DELAY_MS);
    rememberPendingFastValidation(pendingFastValidation);
    pendingReconciliationsRef.current.set(transactionId, {
      timer,
      running: false,
      pendingFastValidation,
      execute,
      onSettled,
    });
    setOptimisticallyReconciledIds((previousIds) => [...previousIds, transactionId]);
  };

  const cancelOptimisticReconciliation = (transactionId: string): boolean => {
    const pendingReconciliation = pendingReconciliationsRef.current.get(transactionId);
    if (isNil(pendingReconciliation) || pendingReconciliation.running) {
      return false;
    }
    clearTimeout(pendingReconciliation.timer);
    pendingReconciliationsRef.current.delete(transactionId);
    forgetPendingFastValidation(transactionId);
    setOptimisticallyReconciledIds((previousIds) => previousIds.filter((id) => id !== transactionId));
    return true;
  };

  const markFastValidationRejected = (transactionId: string) => {
    rejectedFastValidationIdsRef.current.add(transactionId);
  };

  const isFastValidationRejected = (transactionId: string): boolean =>
    rejectedFastValidationIdsRef.current.has(transactionId);

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === TRANSACTION_TABLE_NAME
      ) {
        setShouldFetchState(getTypedCustomSubscriptionValueForRefetch(newData).transactionIds);
      }
    },
    () => {},
    () => {}
  );

  useSubscriptions<OnCreateTransactionSubscription, OnUpdateTransactionSubscription, OnDeleteTransactionSubscription>(
    onCreateTransaction,
    onUpdateTransaction,
    onDeleteTransaction,
    (data) => {
      const newData = omit(data.onCreateTransaction, 'postings') as TransactionWithoutPostings;
      dispatch({ type: 'ADD_TRANSACTION', payload: { transaction: newData } });
    },
    (data) => {
      const newData = omit(data.onUpdateTransaction, 'postings') as TransactionWithoutPostings;
      const storedTransaction = storedTransactionsRef.current?.find((transaction) => transaction.id === newData.id);
      dispatch({ type: 'UPDATE_TRANSACTION', payload: { transaction: newData } });
      if (isIncomingTransactionNewlyReconciled(newData, storedTransaction)) {
        invalidatePayments(queryClient, clientId);
      }
    },
    (data) => {
      const newData = data.onDeleteTransaction as TransactionWithoutPostings;
      dispatch({ type: 'DELETE_TRANSACTION', payload: { id: newData.id } });
    }
  );

  const setShouldFetchState = (transactionIds?: string[]) => {
    dispatch({ type: 'SHOULD_FETCH', payload: { transactionIds } });
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      const transactionIds = state.shouldFetchState.transactionIdsToFetched;
      dispatch({ type: 'IS_FETCHING', payload: { transactionIds } });
      try {
        if (!isNil(transactionIds)) {
          const fetchResults = await Promise.allSettled(transactionIds.map(fetchTransactionIfExists));
          const updatedTransactionsWithBankAccount = fetchResults.reduce(
            (acc: TransactionWithoutPostings[], fetchResult) =>
              fetchResult.status === 'rejected' || isNil(fetchResult.value?.id)
                ? acc
                : [...acc, addBankAccountToTransaction(omit(fetchResult.value, 'postings'), bankAccounts)],
            []
          );
          transactionIds.forEach((id, index) => {
            const fetchResult = fetchResults[index];
            if (fetchResult.status === 'fulfilled' && isNil(fetchResult.value)) {
              dispatch({ type: 'DELETE_TRANSACTION', payload: { id } });
            }
          });
          dispatch({ type: 'FETCHED_BY_ID', payload: { transactions: updatedTransactionsWithBankAccount } });
          // A full reload settles the rows that could not be read: retrying by id would loop on a forbidden one
          const hasUnreadTransaction = fetchResults.some((fetchResult) => fetchResult.status === 'rejected');
          if (hasUnreadTransaction) {
            dispatch({ type: 'SHOULD_FETCH', payload: {} });
          }
          return;
        }
        const transactionResult = await fetchTransactions(clientId!);
        dispatch({ type: 'FETCHED', payload: { transactions: transactionResult } });
      } catch (error) {
        console.error('[transactions] fetching transactions failed', error);
        const message = error instanceof Error ? error.message : String(error);
        dispatch({ type: 'ERROR', payload: { error: message } });
      }
    };
    if (state.shouldFetchState.shouldFetch && !state.fetching && !isFetchingUser && clientId) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchState, state.fetching, isFetchingUser, clientId]);

  const createTransaction = async (
    input: CreateTransactionInput | Omit<CreateTransactionInput, 'clientId' | 'readId'>
  ): Promise<TransactionWithoutPostings> => {
    const customMutation = removeNestedFieldsExcept(createTransactionMutation, undefined, ['postings']);
    const truncatedTransaction = await mutation<TransactionWithoutPostings, CreateTransactionMutationVariables>(
      customMutation,
      {
        input: {
          ...(cleanInputCreate(input) as CreateTransactionInput),
          readId: getReadId(clientId!, TRANSACTION_TABLE_NAME),
          clientId: getTableClientId(clientId!, TRANSACTION_TABLE_NAME),
          ...(isOwner && { writers: [userId!] }),
        },
      }
    );
    return truncatedTransaction;
  };

  const updateTransaction = async (updates: UpdateTransactionInput): Promise<TransactionWithoutPostings> => {
    const customMutation = removeNestedFieldsExcept(updateTransactionMutation, undefined, ['postings']);
    const updateWithVersion = async (version: number | undefined) =>
      await mutation<TransactionWithoutPostings>(customMutation, {
        input: cleanInputUpdate({ ...updates, _version: version }, false),
      });
    const updateWithVersionFromServer = async () => {
      const transaction = await fetchTransaction(updates.id);
      return await updateWithVersion(transaction._version);
    };
    const versionInState = getTransactionFromState(updates.id)?._version;
    if (isNil(versionInState)) {
      return await updateWithVersionFromServer();
    }
    try {
      return await updateWithVersion(versionInState);
    } catch (error) {
      if (!isConditionalCheckFailedGraphQLError(error)) {
        throw error;
      }
      return await updateWithVersionFromServer();
    }
  };

  const deepDeleteTransaction = async (id: string): Promise<MutationStatus | undefined> => {
    if (!financialTransactionsDelete) {
      return undefined;
    }
    return await mutation<MutationStatus, DeleteEntityByIdMutationVariables>(deleteEntityByIdMutation, {
      input: { entityId: id, entity: DeepDeletableEntityType.TRANSACTION },
    });
  };

  const getTransactionFromState = (id: string): TransactionWithoutPostings | undefined => {
    if (state.error) return undefined;
    return (transactions ?? []).find((t) => t.id === id);
  };

  const getTransactionsForLease = (leaseId: string, filterIgnoredTransaction = false): TransactionWithoutPostings[] => {
    if (state.loading || state.error) return [];
    return (transactions ?? []).filter((t) => {
      const ignore = filterIgnoredTransaction && t.status === TransactionStatus.IGNORE;
      return (
        !ignore &&
        t.links &&
        t.links.some((link) => link.linkType === TransactionLinkType.LEASE && link.linkId === leaseId)
      );
    });
  };

  const getSplittedTransactionsForLease = async (leaseId: string): Promise<ExtendedTransaction[]> => {
    const extendedSplittedTransactions: ExtendedTransaction[] = [];
    await Promise.all(
      (transactions ?? []).map(async (transaction) => {
        if (transaction.links) {
          const splitForLease = transaction.links.find(
            (t) => t.linkType === TransactionLinkType.LEASE && t.linkId === leaseId
          );
          if (splitForLease) {
            const extendedTransaction = await addPostingsToTransaction(transaction);
            const leasePostingsTransactions = extendedTransaction.postings.filter(
              (p) => isNil(p.lease) || p.lease.id === leaseId
            );
            extendedSplittedTransactions.push({
              ...transaction,
              amount: splitForLease.amount,
              postings: leasePostingsTransactions,
            });
          }
        }
      })
    );
    return extendedSplittedTransactions;
  };

  const values = useMemo(
    () => ({
      transactions,
      transactionsHistory,
      transactionsToCheck,
      transactionsSnoozed,
      transactionsExcluded,
      transactionRefetch: setShouldFetchState,
      refreshTransaction,
      createTransaction,
      updateTransaction,
      deepDeleteTransaction,
      getTransaction: getTransactionFromState,
      getTransactionsForLease,
      getSplittedTransactionsForLease,
      scheduleOptimisticReconciliation,
      cancelOptimisticReconciliation,
      markFastValidationRejected,
      isFastValidationRejected,
      transactionsError: state.error,
      transactionsLoading: state.loading || loading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, transactions, loading, optimisticallyReconciledIds]
  );

  return <TransactionContext.Provider value={values}>{children}</TransactionContext.Provider>;
};

export const useTransactions = (): TransactionContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<TransactionContext | null>(TransactionContext);

  if (isNil(context)) {
    throw new Error('`useTransactions` hook must be used within a `TransactionContextProvider` component');
  }

  useEffect(() => {
    if (!firstRender.current && isNil(context.transactions)) {
      context.transactionRefetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context as TransactionContext;
};
